import { Button, Card, Icon, Typography } from '@globalmed/atlas-ui';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './OnDemandNotificationsItem.module.css';
import { useInstitutionsStore } from '@/stores/institutions';
import { TAppointmentCamel } from '@/types';
import { COMMUNICATION_CHANNEL_ICONS } from '@/utils/constants';
import { calculateAge } from '@/utils/convertDate';
import { nameFormat } from '@/utils/valuesFormat';

type TProps = {
  onAppointmentDecline: (id: string) => void;
  onAppointmentAccept: (id: string) => void;
  appointment: TAppointmentCamel;
  loading: boolean;
};

export const OnDemandNotificationsItem = ({
  appointment,
  onAppointmentAccept,
  onAppointmentDecline,
  loading,
}: TProps) => {
  const { institutionServiceId, communicationChannel, id } = appointment;
  const { t } = useTranslation();
  const currentInstitution = useInstitutionsStore(
    (state) => state.currentInstitution,
  );
  const serviceName = useMemo(() => {
    const result = currentInstitution?.services?.find(
      (spec) => spec.id === institutionServiceId,
    );
    if (result) {
      return result.name;
    }
    return t('dashboard.unknown-service');
  }, [currentInstitution?.services, t, institutionServiceId]);
  const patientInfo = useMemo(() => {
    const patient = appointment.participants[0].user;
    const fullName = nameFormat(patient?.firstName, patient?.lastName);
    const gender = patient?.gender ? t(patient.gender) : '-';
    const years = `${calculateAge(patient?.dob ?? '')}${t('y')}`;
    return `${fullName}, ${gender} (${years})`;
  }, [appointment.participants, t]);

  return (
    <Card className={styles.card}>
      <div className={styles.info}>
        <div className={styles.serviceName}>
          <Icon
            name={COMMUNICATION_CHANNEL_ICONS[communicationChannel]}
            size="20"
          />
          <Typography
            as="span"
            fontWeight="600"
            fontSize="16px"
            sx={{ lineHeight: '24px' }}
          >
            {serviceName}
          </Typography>
        </div>
        <Typography color="gray">{patientInfo}</Typography>
      </div>
      <div className={styles.btns}>
        <Button
          preIcon="XClose"
          color="destructive"
          variant="secondary"
          onClick={() => onAppointmentDecline(id)}
          disabled={loading}
        />
        <Button
          preIcon="Check"
          color="success"
          variant="secondary"
          onClick={() => onAppointmentAccept(id)}
          loading={loading}
        />
      </div>
    </Card>
  );
};
